import { createSlice } from '@reduxjs/toolkit';
import {
  CLEAR_CHECKED_LAYERS,
  TOGGLE_SPATIAL_LAYER,
} from 'emergency_data/src/actions/actionTypes';
import { saveSchema } from '@rsos/schema-storage';
import createCycleStorage from './createCycleStorage';
import { positionStorage, getLatestDataset } from './useAxonGLNext';

// The number of minutes to wait before removing inactive markers from the map.
const REMOVE_THRESHOLD_MIN = 15;
// The max number of datasets (snapshots) to keep in memory.
const MAX_IN_MEMORY_DATASETS = 3;

const LAYER_KEYS = {
  AXON_BODY_3: 'axon_police_tracking',
  AXON_FLEET_3: 'axon_fleet_tracking',
};

const isRecording = (status = '') => {
  return `${status}`.toLowerCase() === 'recording';
};

const datasetStorage = createCycleStorage({
  maxSize: MAX_IN_MEMORY_DATASETS,
});

const initialState = {
  // Datasets are stored in a cycleStorage object, where each key is an auto
  // incremented index that is used to maintain sort order. The size of
  // datasets is guaranteed to not exceed the maxSize given to cycleStorage.
  // To select the most recent dataset, use the getLatestDataset selector.
  datasets: {},

  // An object storing historical locations by external_id. Each item has a
  // collection of coordinates for previous marker positions which are kept
  // in cycleStorage.
  // The number of previous positions is set by MAX_HISTORICAL_POSITIONS.
  positions: {},

  // Toggle visibility of the spatial layer (and its DeckGL layers).
  visible: true,
};

const normalizeDatasetById = dataset => {
  const byId = {};
  dataset.forEach(d => {
    byId[d.external_id] = d;
  });
  return byId;
};

const axonSlice = createSlice({
  name: 'axonSlice',
  initialState,
  reducers: {
    handleAxon(state, action) {
      const { payload } = action;
      const { data = [] } = payload;
      let currentDataset = getLatestDataset(state.datasets);
      const incomingDataset = normalizeDatasetById(data) || {};

      if (!currentDataset) {
        // This is the first ever dataset, record position per recording object.
        Object.keys(incomingDataset).forEach(key => {
          const spatial = incomingDataset[key];

          if (!isRecording(spatial?.attributes?.device?.status)) {
            return;
          }

          const point = spatial.shape.features[0].geometry;
          positionStorage.clear();
          positionStorage.save(point);
          if (!state.positions[key]) {
            state.positions[key] = {};
          }
          state.positions[key] = {
            key,
            data: positionStorage.dump(),
          };
        });
      } else {
        // Iterate over the current dataset and check against incoming dataset.
        // Save the spatial object positions.
        // Pick out the inactive objects and tag them with expiration time.
        Object.keys(currentDataset).forEach(key => {
          if (incomingDataset[key]) {
            const spatial = incomingDataset[key];

            // Per reqs, only track previous positions if device is recording.
            if (isRecording(spatial?.attributes?.device?.status)) {
              positionStorage.clear();
              const point = spatial.shape.features[0].geometry;
              if (state.positions[key]) {
                positionStorage.load(state.positions[key].data);
              }
              positionStorage.save(point);

              if (!state.positions[key]) {
                state.positions[key] = {};
              }
              state.positions[key] = {
                key,
                data: positionStorage.dump(),
              };
            } else {
              // Reset positions in case it was previously recording.
              delete state.positions[key];
            }
          } else {
            const inactiveObject = currentDataset[key];
            const isAlreadyTracked = inactiveObject.inactive;
            if (isAlreadyTracked) {
              const isExpired = inactiveObject.expires_at < Date.now();
              if (!isExpired) {
                incomingDataset[key] = inactiveObject;
              } else {
                delete state.positions[key];
              }
            } else {
              inactiveObject.inactive = true;
              inactiveObject.expires_at =
                Date.now() + REMOVE_THRESHOLD_MIN * 60 * 1000;

              // Patch the data to reflect an inactive status.
              inactiveObject.attributes.device.status = 'Inactive';
              inactiveObject.attributes.device.battery_level = 'N/A';
              inactiveObject.attributes.device.signal_strength = 'N/A';

              incomingDataset[key] = inactiveObject;
            }
          }
        });
      }

      state.datasets = {
        ...datasetStorage.save(incomingDataset),
      };
    },
  },
  extraReducers: {
    [TOGGLE_SPATIAL_LAYER]: (state, action) => {
      const { isChecked, layerKey } = action;
      if (layerKey === LAYER_KEYS.AXON_BODY_3) {
        state.visible = isChecked;
      }
    },
    [CLEAR_CHECKED_LAYERS]: state => {
      state.visible = false;
    },
  },
});

export const { handleAxon } = axonSlice.actions;
export default axonSlice.reducer;

export const handleAxonRawMessage = (action = {}) => dispatch => {
  const { layer = {} } = action;
  dispatch(saveSchema({ id: layer.key, schema: layer }));
  dispatch(handleAxon(action));
};
